import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Database, AlertTriangle } from "lucide-react";

const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("it-IT", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

// Migrazione una tantum: date di prova, prenotazioni, modulo/privacy e notifiche diventano per anno
export const YearDataMigrationCard = ({ onDone }) => {
  const [p, setP] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = () => api.get("/admin/migrations/year-data").then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!p || (p.done && !result)) return null;

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/migrations/year-data"); setResult(data); toast.success("Migrazione completata"); onDone?.(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); setConfirm(false); }
  };
  const mism = (result || p).counter_mismatches || [];

  return (
    <Card className="p-6 border-2 border-amber-400 bg-amber-50 space-y-3" data-testid="year-migration-card">
      <h3 className="text-xl flex items-center gap-2"><Database size={18} /> {result ? "Migrazione completata" : "Rendi per anno date di prova, modulo e notifiche"}</h3>
      {!result && <p className="text-sm">Oggi date di prova, prenotazioni, modulo, privacy e notifiche sono condivisi tra tutti gli anni. Ogni data e prenotazione va nell'anno scolastico in cui cade (o nell'anno corrente {p.current_year} se quell'anno non esiste); modulo, privacy e notifiche vanno nell'anno corrente. Nessun dato viene cancellato e i contatori dei posti restano come sono.</p>}
      <div data-testid="year-migration-by-year">
        <p className="text-sm font-semibold">{result ? "Assegnati" : "Anteprima"} per anno</p>
        <ul className="text-sm list-disc pl-5">
          {Object.entries((result || p).by_year || {}).sort().map(([y, c]) => <li key={y}><strong>{y}</strong>: {c.slots} date di prova, {c.bookings} prenotazioni</li>)}
          {Object.keys((result || p).by_year || {}).length === 0 && <li>Nessuna data di prova da assegnare</li>}
        </ul>
        {!result && p.settings_to_current?.length > 0 && <p className="text-sm mt-1">Modulo, privacy e notifiche → anno <strong>{p.current_year}</strong></p>}
      </div>
      {!result && (
        <div data-testid="year-migration-vetrina">
          <p className="text-sm font-semibold">Date oggi visibili in vetrina che non lo sarebbero più</p>
          {p.vetrina_lost.length === 0 ? <p className="text-sm text-green-800">Nessuna: la vetrina resta identica.</p> : (
            <ul className="text-sm list-disc pl-5 text-red-800">{p.vetrina_lost.map((s) => <li key={s.id}>{fmt(s.date)} {s.time} · {s.location} → anno {s.year}</li>)}</ul>
          )}
        </div>
      )}
      {result && <p className="text-sm" data-testid="year-migration-counts">Conteggi prima e dopo identici: {Object.entries(result.counts_after).map(([k, v]) => `${k} ${v}`).join(" · ")}</p>}
      {mism.length > 0 && (
        <div data-testid="year-migration-mismatches" className="rounded-md bg-white border border-amber-300 p-3">
          <p className="text-sm font-semibold flex items-center gap-1.5"><AlertTriangle size={14} /> Contatori dei posti che non corrispondono (non modificati)</p>
          <ul className="text-sm list-disc pl-5">{mism.map((m) => <li key={m.id}>{fmt(m.date)} {m.time} · {m.location}: attuale {m.booked_count}, corretto {m.real}</li>)}</ul>
        </div>
      )}
      {!result && <Button data-testid="year-migration-run" onClick={() => setConfirm(true)} className="bg-primary text-primary-foreground">Applica migrazione</Button>}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="year-migration-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Applicare la migrazione?</AlertDialogTitle>
            <AlertDialogDescription>Le date di prova e le prenotazioni verranno assegnate agli anni indicati nell'anteprima. Se i conteggi prima e dopo non tornano, non viene modificato nulla.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="year-migration-confirm" disabled={busy} onClick={(e) => { e.preventDefault(); run(); }}>{busy ? "Migrazione…" : "Conferma"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
